import { useCallback, useEffect, useState } from 'react';
import { FileArrowUp, Warning } from 'shared/icons';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { FormatInt, FormatMoney } from '../../utils/format';
import Button from '../ui/Button';
import ErrorCard from '../ui/ErrorCard';
import FileUploader from '../ui/FileUploader';
import Input from '../ui/Input';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import { useToast } from '../ui/Toast';
import { DASH, FormatDay, LotsTable } from './pricingShared';

/**
 * NOTAS DE ENTRADA — importa o XML da NF-e de compra (um ou vários
 * arquivos), lista as notas importadas e, dentro de cada nota, os itens
 * (lotes) com o vínculo ao SKU. Itens que o importador não conseguiu casar
 * (EAN / código do fornecedor / código = SKU) aparecem primeiro, em "Itens
 * sem produto vinculado", para o vínculo manual — sem ele o lote não entra
 * no custo. O vínculo é do DE-PARA (fornecedor → produto), não do lote.
 */

const DEFAULT_PAGE_SIZE = 20;

const ImportCard = ({ onImported }) => {
    const toast = useToast();
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState([]);

    const importAll = async () => {
        if (files.length === 0) {
            return;
        }
        setBusy(true);
        const outcomes = [];
        for (const file of files) {
            try {
                const xml = await file.text();
                const payload = await api.importPricingNfe(xml);
                const doc = payload?.document || {};
                outcomes.push({
                    name: file.name,
                    ok: true,
                    already: Boolean(payload?.already_imported),
                    number: doc.number,
                    issuer: doc.issuer_name,
                    items: doc.items_count,
                    matched: doc.matched_items,
                });
            } catch (error) {
                outcomes.push({ name: file.name, ok: false, message: error?.message || 'falha ao importar' });
            }
        }
        setResults(outcomes);
        setFiles([]);
        setBusy(false);
        const imported = outcomes.filter((entry) => entry.ok && !entry.already).length;
        if (imported > 0) {
            toast.success(`${imported} nota(s) importada(s)`);
        }
        onImported?.();
    };

    return (
        <div className="space-y-3 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
            <div className="flex items-start gap-2">
                <FileArrowUp size={20} className="mt-0.5 shrink-0 text-mono-500" />
                <div className="text-sm text-mono-700 dark:text-mono-300">
                    <p className="font-medium text-black dark:text-white">Importar XML da NF-e de compra</p>
                    <p className="text-xs text-mono-500">
                        Cada item da nota vira um lote de custo com mercadoria, IPI, alíquota de ICMS e ST lidos do XML.
                        O tratamento de ICMS é sugerido pelo CST/CFOP e confirmado pela regra do SKU/NCM. A mesma chave
                        não é importada duas vezes.
                    </p>
                </div>
            </div>
            <FileUploader
                files={files}
                onFilesChange={setFiles}
                accept=".xml,text/xml,application/xml"
                multiple
                maxFiles={50}
                disabled={busy}
                title="Arraste os XMLs aqui ou selecione"
                subtitle="NF-e modelo 55 (nfeProc ou NFe)"
            />
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-mono-500">
                    {files.length ? `${files.length} arquivo(s) selecionado(s)` : ''}
                </p>
                <Button onClick={importAll} loading={busy} disabled={busy || files.length === 0}>
                    Importar
                </Button>
            </div>
            {results.length > 0 ? (
                <ul className="space-y-1 text-xs">
                    {results.map((entry) => (
                        <li
                            key={entry.name}
                            className={
                                entry.ok ? 'text-mono-700 dark:text-mono-300' : 'text-danger-600 dark:text-danger-400'
                            }
                        >
                            <span className="font-mono">{entry.name}</span>
                            {entry.ok
                                ? ` — ${entry.already ? 'já importada' : 'importada'}: NF nº ${entry.number || DASH} de ${entry.issuer || DASH}, ${FormatInt(entry.matched)}/${FormatInt(entry.items)} itens vinculados`
                                : ` — ${entry.message}`}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

const UnmatchedLots = ({ refreshTick, onChanged }) => {
    const [lots, setLots] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.getPricingLots({ unmatched: 'true', limit: 200 })
            .then((payload) => {
                if (!cancelled) {
                    setLots(Array.isArray(payload?.items) ? payload.items : []);
                    setTotal(Number(payload?.total || 0));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setLots([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [refreshTick]);

    if (loading || lots.length === 0) {
        return null;
    }
    return (
        <div className="space-y-2 rounded-xl border border-warning-200 bg-warning-50/40 p-4 dark:border-warning-900 dark:bg-warning-900/10">
            <div className="flex items-start gap-2">
                <Warning size={18} className="mt-0.5 shrink-0 text-warning-600 dark:text-warning-400" />
                <p className="text-sm text-warning-800 dark:text-warning-200">
                    <strong>{FormatInt(total)} item(ns) de nota sem produto vinculado.</strong> Escolha o produto do
                    Bling no seletor — o de-para fica guardado e a próxima nota do mesmo fornecedor com o mesmo código
                    casa sozinha.
                </p>
            </div>
            <LotsTable lots={lots} onLotUpdated={onChanged} exportFileName="itens-sem-produto" />
        </div>
    );
};

const DocumentItems = ({ documentId, refreshTick, onChanged }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setData(null);
        api.getPricingNfe(documentId)
            .then((payload) => {
                if (!cancelled) {
                    setData(payload);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar a nota.');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [documentId, refreshTick]);
    if (error) {
        return <p className="p-3 text-sm text-danger-600 dark:text-danger-400">{error}</p>;
    }
    if (!data) {
        return <p className="p-3 text-sm text-mono-500">Carregando itens…</p>;
    }
    const doc = data.document;
    return (
        <div className="space-y-2 p-3">
            <p className="text-xs text-mono-500">
                Chave {doc.access_key} · {doc.nat_op || DASH} · emitente {doc.issuer_cnpj} ({doc.issuer_uf || DASH}) →
                destinatário {doc.dest_name || DASH} ({doc.dest_uf || DASH}) · frete {FormatMoney(doc.total_freight)} ·
                desconto {FormatMoney(doc.total_discount)}
            </p>
            <LotsTable
                lots={data.items}
                onLotUpdated={onChanged}
                exportFileName={`nfe-${doc.number || doc.id}-itens`}
            />
        </div>
    );
};

const NfeImports = () => {
    const { checkPermission } = useAuth();
    const canWrite = checkPermission('pricing:write');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [expandedId, setExpandedId] = useState(null);

    const refresh = useCallback(() => setRefreshTick((tick) => tick + 1), []);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        api.getPricingNfeList({ search, limit: pageSize, offset: (page - 1) * pageSize })
            .then((payload) => {
                if (!cancelled) {
                    setRows(Array.isArray(payload?.items) ? payload.items : []);
                    setTotal(Number(payload?.total || 0));
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar as notas.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setHasLoadedOnce(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [search, page, pageSize, refreshTick]);

    const applySearch = () => {
        const term = searchInput.trim();
        setSearchInput(term);
        if (term !== search) {
            setSearch(term);
            setPage(1);
        }
    };

    const columns = [
        {
            key: 'emitted_at',
            label: 'Emissão',
            getValue: (row) => String(row.emitted_at || '').slice(0, 10),
            render: (row) => <span className="font-mono text-xs">{FormatDay(row.emitted_at)}</span>,
        },
        {
            key: 'number',
            label: 'NF-e',
            getValue: (row) => row.number,
            render: (row) => (
                <span className="font-mono text-sm">
                    nº {row.number || DASH}
                    {row.series ? `/${row.series}` : ''}
                </span>
            ),
        },
        {
            key: 'issuer',
            label: 'Fornecedor',
            getValue: (row) => `${row.issuer_name || ''} ${row.issuer_cnpj || ''}`,
            render: (row) => (
                <div className="min-w-0">
                    <p
                        className="truncate text-sm font-medium text-black dark:text-white"
                        title={row.issuer_name || ''}
                    >
                        {row.issuer_name || DASH}
                    </p>
                    <p className="font-mono text-[11px] text-mono-500">
                        {row.issuer_cnpj || DASH}
                        {row.issuer_uf ? ` · ${row.issuer_uf}` : ''}
                    </p>
                </div>
            ),
        },
        {
            key: 'items',
            label: 'Itens vinculados',
            align: 'center',
            getValue: (row) => Number(row.matched_items || 0),
            render: (row) => (
                <span
                    className={`tabular-nums ${row.matched_items < row.items_count ? 'font-semibold text-warning-700 dark:text-warning-400' : ''}`}
                >
                    {FormatInt(row.matched_items)}/{FormatInt(row.items_count)}
                </span>
            ),
        },
        {
            key: 'total_products',
            label: 'Produtos',
            align: 'right',
            getValue: (row) => Number(row.total_products || 0),
            render: (row) => <span className="tabular-nums">{FormatMoney(row.total_products)}</span>,
        },
        {
            key: 'total_ipi',
            label: 'IPI',
            align: 'right',
            getValue: (row) => Number(row.total_ipi || 0),
            render: (row) => <span className="tabular-nums">{FormatMoney(row.total_ipi)}</span>,
        },
        {
            key: 'total_st',
            label: 'ST',
            align: 'right',
            getValue: (row) => Number(row.total_st || 0),
            render: (row) => <span className="tabular-nums">{FormatMoney(row.total_st)}</span>,
        },
        {
            key: 'total_icms',
            label: 'ICMS',
            align: 'right',
            getValue: (row) => Number(row.total_icms || 0),
            render: (row) => <span className="tabular-nums">{FormatMoney(row.total_icms)}</span>,
        },
        {
            key: 'total_nf',
            label: 'Total NF',
            align: 'right',
            getValue: (row) => Number(row.total_nf || 0),
            render: (row) => <span className="font-semibold tabular-nums">{FormatMoney(row.total_nf)}</span>,
        },
    ];

    return (
        <div className="space-y-4">
            {canWrite ? <ImportCard onImported={refresh} /> : null}
            <UnmatchedLots refreshTick={refreshTick} onChanged={refresh} />
            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] max-w-md flex-1">
                    <Input
                        label="Buscar nota"
                        type="search"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        onBlur={applySearch}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                applySearch();
                            }
                        }}
                        placeholder="Número, fornecedor, CNPJ ou chave..."
                    />
                </div>
            </div>
            {error ? (
                <ErrorCard
                    title="Erro ao carregar notas"
                    message={error}
                    action="Tentar novamente"
                    actionFn={refresh}
                />
            ) : !hasLoadedOnce ? (
                <SkeletonTable columns={8} rows={6} />
            ) : (
                <StandardTable
                    rows={rows}
                    columns={columns}
                    rowKey={(row) => row.id}
                    onRowClick={(row) => setExpandedId((prev) => (prev === row.id ? null : row.id))}
                    rowClassName={(row) =>
                        expandedId === row.id ? 'bg-mono-50 dark:bg-mono-900/60' : 'cursor-pointer'
                    }
                    renderExpandedRow={(row) =>
                        expandedId === row.id ? (
                            <DocumentItems documentId={row.id} refreshTick={refreshTick} onChanged={refresh} />
                        ) : null
                    }
                    countLabel="notas"
                    emptyMessage={search ? `Nenhuma nota para “${search}”.` : 'Nenhuma NF-e de compra importada ainda.'}
                    exportFileName="nfe-compra"
                    searchEnabled={false}
                    paginationMode="server"
                    totalItems={total}
                    currentPage={page}
                    onPageChange={setPage}
                    itemsPerPage={pageSize}
                    onItemsPerPageChange={setPageSize}
                    defaultPageSize={DEFAULT_PAGE_SIZE}
                    pageSizeOptions={[20, 50, 100]}
                />
            )}
        </div>
    );
};

export default NfeImports;
